// ⌘K command palette (DevRobot UI kit): jump to pages, projects and servers, or run actions.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { CornerDownLeft, FolderGit2, Keyboard, MoonStar, Plus, Rocket, Search, SearchX, Server, type LucideIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { ProjectDTO, TargetDTO, VpsDTO } from '@devrobot/shared';
import { api } from '../api/client';
import { qk } from '../api/queries';
import { useUser } from '../api/user';
import { toggleTheme } from '../kit/theme';
import { ALL_NAV } from './layout/nav';
import { RobotMark } from './Logo';
import { statusInfo } from '../lib/status';

export const openCommand = () => window.dispatchEvent(new Event('dr:command'));
export const openKeyboardHelp = () => (document.getElementById('keyboard-shortcuts') as HTMLDialogElement | null)?.showModal();

interface Command {
  id: string;
  label: string;
  icon: LucideIcon;
  hint?: string;
  run: () => void;
}

function score(label: string, q: string): number {
  if (!q) return 1;
  const l = label.toLowerCase();
  const at = l.indexOf(q);
  if (at === 0) return 4;
  if (at > 0) return /[\s\-_/.]/.test(l[at - 1]) ? 3 : 2;
  let j = 0;
  for (const c of l) if (c === q[j]) j++;
  return j === q.length ? 1 : 0;
}

export default function CommandPalette() {
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const user = useUser();
  // Loaded the first time the palette opens (same cache as the Projects and VPS pages).
  const projects = useQuery({ queryKey: qk.projects, queryFn: () => api.get<ProjectDTO[]>('/api/v1/projects'), enabled: opened });
  const servers = useQuery({ queryKey: qk.vpsList, queryFn: () => api.get<VpsDTO[]>('/api/v1/vps'), enabled: opened });
  const deployments = useQuery({ queryKey: qk.deployments(), queryFn: () => api.get<TargetDTO[]>('/api/v1/targets'), enabled: opened });

  useEffect(() => {
    const show = () => {
      setQuery('');
      setActive(0);
      setOpened(true);
      if (!dialog.current?.open) dialog.current?.showModal();
      requestAnimationFrame(() => input.current?.focus());
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (dialog.current?.open) dialog.current.close();
        else show();
      }
    };
    window.addEventListener('dr:command', show);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('dr:command', show);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const actions: Command[] = [
      { id: 'act-theme', label: 'Switch between light and dark', icon: MoonStar, hint: 'Theme', run: () => void toggleTheme() },
      ...(user.platformAdmin ? [{ id: 'act-vps', label: 'Add a VPS', icon: Plus, hint: 'VPS', run: () => void navigate('/vps/new') }] : []),
      { id: 'act-keys', label: 'Show keyboard shortcuts', icon: Keyboard, hint: '?', run: openKeyboardHelp },
    ];
    const all: { group: string; limit: number; items: Command[] }[] = [
      { group: 'Pages', limit: 8, items: ALL_NAV.map((n) => ({ id: `page-${n.to}`, label: n.label, icon: n.icon, hint: `g ${n.key}`, run: () => void navigate(n.to) })) },
      { group: 'Actions', limit: 5, items: actions },
      {
        group: 'Projects',
        limit: q ? 8 : 4,
        items: (projects.data ?? []).map((p) => ({ id: `project-${p.key}`, label: p.name === p.key ? p.key : `${p.name} · ${p.key}`, icon: FolderGit2, hint: p.role, run: () => void navigate(`/projects/${encodeURIComponent(p.key)}`) })),
      },
      {
        group: 'Deployments',
        limit: q ? 8 : 4,
        items: (deployments.data ?? []).map((t) => ({
          id: `deployment-${t.id}`,
          label: `${t.project.name} · ${t.environment}`,
          icon: Rocket,
          hint: t.vps.name,
          run: () => void navigate(`/targets/${encodeURIComponent(t.id)}`),
        })),
      },
      {
        group: 'Servers',
        limit: q ? 8 : 3,
        items: (servers.data ?? []).map((v) => ({ id: `vps-${v.id}`, label: `${v.name} · ${v.host}`, icon: Server, hint: statusInfo('connection', v.connection).label, run: () => void navigate(`/vps/${encodeURIComponent(v.id)}`) })),
      },
    ];
    return all
      .map((g) => ({
        ...g,
        items: g.items
          .map((item) => ({ item, s: score(item.label, q) }))
          .filter((x) => x.s > 0)
          .sort((a, b) => b.s - a.s)
          .slice(0, g.limit)
          .map((x) => x.item),
      }))
      .filter((g) => g.items.length);
  }, [query, projects.data, servers.data, deployments.data, navigate, user.platformAdmin]);
  const flat = groups.flatMap((g) => g.items);

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, query]);

  const run = (item: Command | undefined) => {
    if (!item) return;
    dialog.current?.close();
    item.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (flat.length ? (i + step + flat.length) % flat.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(flat[active]);
    }
  };

  const loading = projects.isLoading || servers.isLoading;

  return (
    <dialog ref={dialog} className="modal modal-top sm:modal-middle" aria-label="Command palette">
      <div className="modal-box mx-auto mt-[max(8vh,calc(var(--dr-safe-top)+0.75rem))] w-[min(40rem,calc(100vw-1.5rem))] max-w-none overflow-hidden rounded-box p-0 sm:mt-0">
        <label className="flex items-center gap-3 border-b border-hairline px-4">
          <Search className="size-5 text-base-content/45" aria-hidden="true" />
          <input
            ref={input}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            className="h-14 w-full bg-transparent text-[15px] outline-none placeholder:text-base-content/40"
            placeholder="Search pages, projects, servers, actions…"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={flat[active] ? `cmd-${flat[active].id}` : undefined}
            aria-label="Search DevRobot"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {loading ? <span className="loading loading-spinner loading-xs text-base-content/40" aria-label="Loading projects and servers" /> : null}
          <kbd className="kbd kbd-sm max-sm:hidden">esc</kbd>
        </label>
        <div ref={list} id="cmdk-list" role="listbox" className="max-h-[min(60vh,26rem)] overflow-y-auto p-2">
          {flat.length === 0 ? (
            <div className="grid place-items-center gap-2 px-6 py-12 text-center">
              <SearchX className="size-6 text-base-content/35" aria-hidden="true" />
              <p className="font-medium">Nothing matches “{query}”</p>
              <p className="text-sm text-base-content/55">Try a project key, a server name, or a page like Settings.</p>
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.group} role="group" aria-label={g.group} className="pb-1">
                <p className="px-3 pt-2 pb-1 text-[11px] font-semibold tracking-[0.08em] text-base-content/45 uppercase">{g.group}</p>
                {g.items.map((item) => {
                  const i = flat.indexOf(item);
                  const Icon = item.icon;
                  const selected = i === active;
                  return (
                    <button
                      key={item.id}
                      id={`cmd-${item.id}`}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      tabIndex={-1}
                      onMouseMove={() => !selected && setActive(i)}
                      onClick={() => run(item)}
                      className="cmd-item flex w-full items-center gap-3 rounded-field px-3 py-2.5 text-left text-sm transition-colors duration-150"
                    >
                      <span className={`grid size-8 shrink-0 place-items-center rounded-lg transition-colors duration-150 ${selected ? 'dr-tint-primary' : 'bg-base-200 text-base-content/70'}`}>
                        <Icon className="size-4" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium">{item.label}</span>
                      {item.hint ? <span className="text-xs text-base-content/50">{item.hint}</span> : null}
                      <CornerDownLeft className={`size-4 text-base-content/40 transition-opacity duration-150 ${selected ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="flex items-center gap-4 border-t border-hairline px-4 py-2.5 text-xs text-base-content/55 max-sm:hidden">
          <span className="inline-flex items-center gap-1.5">
            <kbd className="kbd kbd-xs">↑</kbd>
            <kbd className="kbd kbd-xs">↓</kbd> Move
          </span>
          <span className="inline-flex items-center gap-1.5">
            <kbd className="kbd kbd-xs">↵</kbd> Open
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5">
            <RobotMark className="size-4" /> DevRobot
          </span>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="submit" tabIndex={-1}>
          close
        </button>
      </form>
    </dialog>
  );
}
